import React, { createContext, useContext, useState, useEffect } from 'react';
import {  apiClient } from '../api/api';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const [selectedShopId, setSelectedShopId] = useState(null);
  const [shops, setShops] = useState([]);
  const [dateRange, setDateRange] = useState("Last 7 Days");

  const refreshShops = async (userId) => {
    const idToUse = userId || user?._id || user?.id;
    if (!idToUse) return;
    try {
      const resp = await apiClient.get(`/api/shops/get?userId=${idToUse}`);
      if (resp.data?.shops) {
        setShops(resp.data.shops);
      }
    } catch (err) {
      console.error("[AuthContext] Failed to refresh shops", err);
    }
  };

  // Fetch profile whenever authenticated but user data is missing
  useEffect(() => {
    const fetchProfileData = async () => {
      if (isAuthenticated && !user) {
        try {
          const profileResp = await apiClient.get('/user/profile');
          if (profileResp.data?.user) {
            const userData = profileResp.data.user;
            setUser(userData);
            refreshShops(userData.id);
            if (userData.brand_id && !selectedShopId) {
              setSelectedShopId(userData.brand_id);
            }
          }
        } catch (profileErr) {
          console.warn("[AuthContext] Failed to fetch profile after auth", profileErr);
        }
      }
    };

    // Also check reload auth on initial mount
    const checkReloadAuth = async () => {
      try {
        const { data } = await apiClient.get('/user/profile');
        if (data && data.user) {
          setIsAuthenticated(true);
          setUser(data.user);
          refreshShops(data.user.id);
          if (data.user.brand_id) {
            setSelectedShopId(data.user.brand_id);
          }
        } else {
          setIsAuthenticated(false);
          localStorage.removeItem('is_tiktok_authenticated');
          setUser(null);
        }
      } catch (err) {
        console.error("[AuthContext] Reload auth check failed:", err);
        setIsAuthenticated(false);
        localStorage.removeItem('is_tiktok_authenticated');
        setUser(null);
      }
      setLoading(false);
    };

    if (loading) {
      checkReloadAuth();
    } else {
      fetchProfileData();
    }
  }, [isAuthenticated, user, loading, selectedShopId]);

  // Only for reload-persistence: set flag on login
  const login = (userData) => {
    localStorage.setItem('is_tiktok_authenticated', 'true');
    setUser(userData);
    setIsAuthenticated(true);
    
    const userId = userData.id || userData._id;
    if (userId) {
      refreshShops(userId);
    }
    
    if (userData.brand_id) {
      setSelectedShopId(userData.brand_id);
    }
  };

  const logout = async () => {
    try {
      await apiClient.post('/logout', {}, { withCredentials: true });
    } catch (e) {
      // ignore error, still clear local state
    }
    localStorage.removeItem('is_tiktok_authenticated');
    setUser(null);
    setIsAuthenticated(false);
    setSelectedShopId(null);
  };

  const getAccessToken = () => {
    return localStorage.getItem('tiktok_access_token');
  };

  const value = {
    isAuthenticated,
    user,
    loading,
    login,
    logout,
    getAccessToken,
    setIsAuthenticated,
    selectedShopId,
    setSelectedShopId,
    shops,
    refreshShops,
    dateRange,
    setDateRange,
    updateShopStatus: (shopId, newStatus) => {
        setShops(prev => prev.map(s => s._id === shopId ? { ...s, activationStatus: newStatus } : s));
    }
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}; 